// Catalog services as charge options for the rate panel's "Select Charge..." dropdown.
// genAccessorials is a fixed per-algo list and never sees the shipper's catalog.

export const SERVICE_CHARGE_TYPE = 'SERVICE_RATE';

const ACCESSORIAL_GROUP = 'Accessorials';
const SERVICE_GROUP = 'Services';

const clean = (value) => (value === null || value === undefined ? '' : `${value}`.trim());

// Mirrors priceServiceLines (pricing/algos/utils.js) so a charge here costs what the pricer bills.
const findServiceRate = (serviceRateRows, code) => {
    const wanted = clean(code).toUpperCase();
    if (!wanted) return null;

    return (
        (serviceRateRows || []).find((row) => {
            const rateCode = clean(row?.dimensions?.code || row?.qbo_tag).toUpperCase();
            return !!rateCode && rateCode === wanted;
        }) || null
    );
};

const billedCodes = (billedCharges) =>
    new Set(
        (billedCharges || [])
            .filter((charge) => charge?.type === SERVICE_CHARGE_TYPE)
            .map((charge) => clean(charge?.code).toUpperCase())
            .filter(Boolean)
    );

// `serviceRateRows` is this perspective's tariff, and an override is party-specific — requiring a
// matching row is what scopes the list to the shipper/carrier/contractor combo on screen.
// `billedCharges` drops services the leg already charges, which would otherwise double-bill.
export const buildServiceChargeOptions = (services, serviceRateRows, billedCharges) => {
    const alreadyBilled = billedCodes(billedCharges);

    return (services || [])
        .map((service) => {
            const code = clean(service?.code);
            if (!code || alreadyBilled.has(code.toUpperCase())) return null;

            const match = findServiceRate(serviceRateRows, code);
            if (!match || !Number.isFinite(match.rate)) return null;

            return {
                type: SERVICE_CHARGE_TYPE,
                code,
                label: clean(service?.description) || code,
                rate: match.rate,
                quantity: 1,
                total: match.rate,
                qbo_tag: clean(match.qbo_tag) || code,
                // Read back off the options array by Charge.js.
                adornment: '$',
                isPercentage: false,
            };
        })
        .filter(Boolean)
        .sort((l, r) => l.label.localeCompare(r.label));
};

// MUI groups consecutive items only, so a group must arrive contiguous or its header renders twice.
export const mergeChargeOptions = (accessorialOptions, serviceOptions) => [
    ...(accessorialOptions || []),
    ...(serviceOptions || []),
];

// No accessorial algo defines SERVICE_RATE, so type is a safe discriminator.
export const chargeOptionGroup = (option) => (option?.type === SERVICE_CHARGE_TYPE ? SERVICE_GROUP : ACCESSORIAL_GROUP);
